import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Search, Filter, ChevronLeft, Stethoscope, Microscope, Building2, Pill, Ambulance, type LucideIcon } from 'lucide-react';
import { patientApi } from '../api';
import type { MarketplaceProvider, MarketplaceProviderType } from '../types';
import ProviderCard from '../components/ProviderCard';
import Spinner from '../../../components/ui/Spinner';

const TYPE_CONFIG: Record<string, { label: string; plural: string; icon: LucideIcon; apiType: MarketplaceProviderType }> = {
  doctor: { label: 'Doctor', plural: 'Doctors', icon: Stethoscope, apiType: 'DOCTOR' },
  lab: { label: 'Lab', plural: 'Labs & Diagnostics', icon: Microscope, apiType: 'LAB' },
  hospital: { label: 'Hospital', plural: 'Hospitals', icon: Building2, apiType: 'HOSPITAL' },
  pharmacy: { label: 'Pharmacy', plural: 'Pharmacies', icon: Pill, apiType: 'PHARMACY' },
  ambulance: { label: 'Ambulance', plural: 'Ambulance Services', icon: Ambulance, apiType: 'AMBULANCE' },
};

export default function ProviderListing() {
  const { type = 'doctor' } = useParams<{ type: string }>();
  const navigate = useNavigate();
  const config = TYPE_CONFIG[type] || TYPE_CONFIG.doctor;

  const [providers, setProviders] = useState<MarketplaceProvider[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [verifiedOnly, setVerifiedOnly] = useState(false);
  const [page, setPage] = useState(1);
  const limit = 12;

  useEffect(() => {
    setPage(1);
    setSearch('');
    setVerifiedOnly(false);
  }, [type]);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    patientApi.getMarketplaceProviders({
      type: config.apiType,
      search: search.trim() || undefined,
      verified: verifiedOnly ? 'true' : undefined,
      page,
      limit,
    })
      .then(r => { if (alive) { setProviders(r.providers); setTotal(r.total); } })
      .catch(() => { if (alive) { setProviders([]); setTotal(0); } })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [config.apiType, search, verifiedOnly, page]);

  const totalPages = Math.ceil(total / limit);
  const Icon = config.icon;

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <button
          onClick={() => navigate('/patient/marketplace')}
          className="p-2 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
        >
          <ChevronLeft className="w-5 h-5" />
        </button>
        <div className="flex items-center gap-2">
          <Icon className="w-5 h-5 text-teal-600" />
          <div>
            <h1 className="text-xl font-bold text-gray-900">{config.plural}</h1>
            <p className="text-xs text-gray-500">{total} provider{total !== 1 ? 's' : ''} available</p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            className="input pl-9 !text-sm"
            placeholder={`Search ${config.plural.toLowerCase()}...`}
            value={search}
            onChange={e => { setSearch(e.target.value); setPage(1); }}
          />
        </div>
        <button
          onClick={() => { setVerifiedOnly(!verifiedOnly); setPage(1); }}
          className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
            verifiedOnly ? 'bg-blue-50 text-blue-700 border border-blue-200' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
          }`}
        >
          <Filter className="w-4 h-4" />
          Verified only
        </button>
      </div>

      {/* Results */}
      {loading ? (
        <div className="flex justify-center py-12"><Spinner /></div>
      ) : providers.length === 0 ? (
        <div className="text-center py-12">
          <Icon className="w-10 h-10 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500">No {config.plural.toLowerCase()} found</p>
          {(search || verifiedOnly) && (
            <button
              onClick={() => { setSearch(''); setVerifiedOnly(false); }}
              className="text-sm text-teal-600 hover:text-teal-700 mt-2"
            >
              Clear filters
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          {providers.map(p => <ProviderCard key={p.id} provider={p} />)}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2">
          <button
            disabled={page <= 1}
            onClick={() => setPage(page - 1)}
            className="px-3 py-1.5 rounded-lg text-sm font-medium bg-gray-100 text-gray-600 hover:bg-gray-200 disabled:opacity-40"
          >
            Previous
          </button>
          <span className="text-sm text-gray-500">Page {page} of {totalPages}</span>
          <button
            disabled={page >= totalPages}
            onClick={() => setPage(page + 1)}
            className="px-3 py-1.5 rounded-lg text-sm font-medium bg-gray-100 text-gray-600 hover:bg-gray-200 disabled:opacity-40"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
}
